import type { Rectangle } from './zoneALEcran';

/**
 * Où poser la bulle de la visite à côté de sa zone (ticket 33) : du côté demandé s'il y tient
 * dans la fenêtre, sinon d'un autre, sinon en bas au centre. La flèche vise le milieu de la
 * zone. Géométrie pure : elle reçoit des tailles, elle rend des positions.
 */

export type Cote = 'haut' | 'bas' | 'gauche' | 'droite';

export interface Place {
  left: number;
  top: number;
  /** Le côté où la bulle est posée ; `null` : aucun ne tient, pas de flèche. */
  cote: Cote | null;
  /** Où tombe la flèche le long du bord de la bulle, en pixels depuis son coin. */
  fleche: number;
}

const ECART = 22;
const MARGE = 16;

export function placeDeLaBulle(
  zone: Rectangle,
  bulle: { largeur: number; hauteur: number },
  prefere: Cote,
  fenetre: { largeur: number; hauteur: number }
): Place {
  const ordre: Cote[] = [prefere, 'bas', 'haut', 'droite', 'gauche'].filter(
    (c, i, tous): c is Cote => tous.indexOf(c) === i
  );
  const milieuX = zone.x + zone.largeur / 2;
  const milieuY = zone.y + zone.hauteur / 2;
  const borneX = (l: number) => Math.max(MARGE, Math.min(fenetre.largeur - bulle.largeur - MARGE, l));
  const borneY = (t: number) => Math.max(MARGE, Math.min(fenetre.hauteur - bulle.hauteur - MARGE, t));

  for (const cote of ordre) {
    let left: number;
    let top: number;
    if (cote === 'bas' || cote === 'haut') {
      top = cote === 'bas' ? zone.y + zone.hauteur + ECART : zone.y - bulle.hauteur - ECART;
      if (top < MARGE || top + bulle.hauteur > fenetre.hauteur - MARGE) continue;
      left = borneX(milieuX - bulle.largeur / 2);
      const fleche = Math.max(24, Math.min(bulle.largeur - 40, milieuX - left - 8));
      return { left, top, cote, fleche };
    }
    left = cote === 'droite' ? zone.x + zone.largeur + ECART : zone.x - bulle.largeur - ECART;
    if (left < MARGE || left + bulle.largeur > fenetre.largeur - MARGE) continue;
    top = borneY(milieuY - bulle.hauteur / 2);
    const fleche = Math.max(24, Math.min(bulle.hauteur - 40, milieuY - top - 8));
    return { left, top, cote, fleche };
  }

  // Rien ne tient (une zone qui couvre presque tout l'écran) : en bas, au centre, sans flèche.
  return {
    left: borneX((fenetre.largeur - bulle.largeur) / 2),
    top: fenetre.hauteur - bulle.hauteur - MARGE,
    cote: null,
    fleche: 0,
  };
}
